import { createApp, watch, watchEffect } from "vue";
import { createPinia } from "pinia";
import { CoralogixRum, CoralogixEventType } from "@coralogix/browser";
import type { EditableCxRumEvent, BeforeSendResult } from "@coralogix/browser";
import App from "./App.vue";
import router from "@/_helpers/router";
import { isExpectedApiError, isThirdPartyError } from "@/_helpers/rum";
import { i18n } from "@/plugins/i18n";
import { createPersistedState } from "pinia-plugin-persistedstate";
import "@/assets/style.css";
import vuetify from "./plugins/vuetify";
import http from "@/plugins/https";
import VueNativeSock from "vue-native-websocket-vue3";
import { useSocketStore } from "@/_store/websocket.module";
import { useWorkspaceStore } from "@/_store/workspace.module";
import { useAccountStore } from "@/_store/account.module";
import { createGtm } from "@gtm-support/vue-gtm";
import "moment/locale/es";
import "moment/locale/en-gb";
import "moment/locale/it";
import "moment/locale/fr";
import "moment/locale/fr-ca";
import "moment/locale/de";
import "moment/locale/pt-br";

const productionEnvs: ImportMetaEnv["VITE_CLIENT_ENV"][] = ["secure", "secure-ca", "secure-eu"];
const isProduction = productionEnvs.includes(import.meta.env.VITE_CLIENT_ENV);
const rumEnvironments: ImportMetaEnv["VITE_RUM_ENV"][] = [
  "prod-us",
  "prod-eu",
  "prod-ca",
  "qa",
  "dev",
  "beta",
];
const rumEnvironment = import.meta.env.VITE_RUM_ENV;
const rumEnabled = rumEnvironments.includes(rumEnvironment);
// Third-party analytics / marketing / captcha domains — do not report these to RUM.
const rumIgnoredUrls = [
  /googletagmanager\.com/i,
  /google-analytics\.com/i,
  /gtag\/js/i,
  /gtm/i,
  /hockeystack/i,
  /stackadapt\.com/i,
  /recaptcha/i,
  /gstatic\.com\/recaptcha/i,
];

const rumIgnoredErrors = [
  // Benign browser noise (well-known non-error), not worth reporting
  /ResizeObserver loop/i,
  // Auth failures the HTTP interceptor already handles (refresh/redirect).
  // NOTE: this matches by message only, so it drops the 401 *error* event from
  // ANY host, not just our API. Network events are filtered per-host in beforeSend.
  /Request failed with status code 401/i,
  // Stackless (`stack: null`, source = unhandledrejection), so no origin rule can
  // reach it — a message match is the only option. Not raised by our code.
  /Object Not Found Matching Id:/i,
];

const rumApiHost = import.meta.env.VITE_API_HOST;

/**
 * Drops events the app already handles by design so they don't bury genuine errors in
 * RUM. Both rules, and the trade-offs they carry, are documented in `@/_helpers/rum`.
 */
const rumBeforeSend = (event: EditableCxRumEvent): BeforeSendResult => {
  if (
    event.event_context.type === CoralogixEventType.NETWORK_REQUEST &&
    isExpectedApiError(event.network_request_context, rumApiHost)
  ) {
    return null;
  }
  if (event.event_context.type === CoralogixEventType.ERROR && isThirdPartyError(event)) {
    return null;
  }
  return event;
};

const pinia = createPinia();
pinia.use(createPersistedState());
const app = createApp(App);

if (rumEnabled) {
  CoralogixRum.init({
    public_key: import.meta.env.VITE_CORALOGIX_PUBLIC_KEY,
    // `application` and `version` MUST match the source-map upload (-a / -v) in
    // gitlab-ci-files/build.yml, or Coralogix cannot de-minify RUM stack traces.
    application: rumEnvironment!,
    version: import.meta.env.VITE_RUM_RELEASE ?? "3.8.5",
    coralogixDomain: "US1",
    ignoreUrls: rumIgnoredUrls,
    ignoreErrors: rumIgnoredErrors,
    beforeSend: rumBeforeSend,
    environment: import.meta.env.VITE_CLIENT_ENV,
  });
}

app.use(pinia);
app.use(router);
app.use(vuetify);
app.use(http);
app.use(i18n);
app.use(
  createGtm({
    id: "GTM-566LFN7", // Your GTM single container ID or array of container ids ['GTM-xxxxxx', 'GTM-yyyyyy'] or array of objects [{id: 'GTM-xxxxxx', queryPararms: { gtm_auth: 'abc123', gtm_preview: 'env-4', gtm_cookies_win: 'x'}}, {id: 'GTM-yyyyyy', queryParams: {gtm_auth: 'abc234', gtm_preview: 'env-5', gtm_cookies_win: 'x'}}]
    defer: false, // Script can be set to `defer` to speed up page load at the cost of less accurate results (in case visitor leaves before script is loaded, which is unlikely but possible). Defaults to false, so the script is loaded `async` by default
    compatibility: false, // Will add `async` and `defer` to the script tag to not block requests for old browsers that do not support `async`
    enabled: isProduction, // defaults to true. Plugin can be disabled by setting this to false for Ex: enabled: !!GDPR_Cookie (optional)
    debug: false, // Whether or not display console logs debugs (optional)
    loadScript: true, // Whether or not to load the GTM Script (Helpful if you are including GTM manually, but need the dataLayer functionality in your components) (optional)
    vueRouter: router, // Pass the router instance to automatically sync with router (optional)
    ignoredViews: [], // Don't trigger events for specified router names (optional)
    trackOnNextTick: false, // Whether or not call trackView in Vue.nextTick
  })
);
const socketStore = useSocketStore(app);

app.use(VueNativeSock, `${import.meta.env.VITE_WEBSOCKET_HOST}/websocket`, {
  format: "",
  store: socketStore,
  reconnection: true,
  reconnectionAttempts: 100,
  reconnectionDelay: 3000,
});

watch([() => useWorkspaceStore().workspace.workspaceId, () => useAccountStore().token], () =>
  socketStore.syncSessionRegistration()
);

watchEffect(() => {
  document.documentElement.lang = i18n.global.locale.value;
});

app.mount("#app");
